export interface NodeTransform {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface CornerResizeResult {
    transforms: NodeTransform[];
    appliedDx: number;
    appliedDy: number;
}

const MIN = 20;
const GAP = 12;

const near = (a: number, b: number) =>
    Math.abs(a - b) <= 1.5;

const snap = (v: number) =>
    Math.round(v);

export function resizeSiblingLayout(
    nodeId: number,
    dx: number,
    dy: number,
    initialTransforms: NodeTransform[],
): CornerResizeResult {
 if (
        !Number.isFinite(dx) ||
        !Number.isFinite(dy) ||
        !Number.isInteger(nodeId) ||
        nodeId < 0 ||
        nodeId >= initialTransforms.length
    ) {
        return {
            transforms: initialTransforms.map(
                (t) => ({ ...t }),
            ),
            appliedDx: 0,
            appliedDy: 0,
        };
    }

    const validTransforms =
        initialTransforms.every(
            (t) =>
                Number.isFinite(t.x) &&
                Number.isFinite(t.y) &&
                Number.isFinite(t.width) &&
                Number.isFinite(t.height),
        );

    if (!validTransforms) {
        return {
            transforms: initialTransforms.map(
                (t) => ({ ...t }),
            ),
            appliedDx: 0,
            appliedDy: 0,
        };
    }
    let ts = initialTransforms.map((t) => ({
        ...t,
    }));

    const n = ts.length;

    const moveVLine = (
        lineX: number,
        delta: number,
        seedLo: number,
        seedHi: number,
    ): { c: number; rIdx: number[] } => {
        let lo = seedLo;
        let hi = seedHi;

        const inBand = (i: number) =>
            ts[i].y < hi - 1 &&
            ts[i].y + ts[i].height > lo + 1;

        const L = new Set<number>();
        const R = new Set<number>();

        let changed = true;

        while (changed) {
            changed = false;

            for (let i = 0; i < n; i++) {
                if (
                    !L.has(i) &&
                    near(
                        ts[i].x + ts[i].width,
                        lineX,
                    ) &&
                    inBand(i)
                ) {
                    L.add(i);

                    lo = Math.min(
                        lo,
                        ts[i].y,
                    );

                    hi = Math.max(
                        hi,
                        ts[i].y + ts[i].height,
                    );

                    changed = true;
                }

                if (
                    !R.has(i) &&
                    near(
                        ts[i].x,
                        lineX + GAP,
                    ) &&
                    inBand(i)
                ) {
                    R.add(i);

                    lo = Math.min(
                        lo,
                        ts[i].y,
                    );

                    hi = Math.max(
                        hi,
                        ts[i].y + ts[i].height,
                    );

                    changed = true;
                }
            }
        }

        if (!R.size) {
            return {
                c: 0,
                rIdx: [],
            };
        }

        let c = delta;

        L.forEach((i) => {
            c = Math.max(
                -(ts[i].width - MIN),
                c,
            );
        });

        R.forEach((i) => {
            c = Math.min(
                ts[i].width - MIN,
                c,
            );
        });

        if (c === 0) {
            return {
                c: 0,
                rIdx: [],
            };
        }

        ts = ts.map((t, i) => {
            if (L.has(i)) {
                return {
                    ...t,
                    width: snap(
                        t.width + c,
                    ),
                };
            }

            if (R.has(i)) {
                return {
                    ...t,
                    x: snap(t.x + c),
                    width: snap(
                        t.width - c,
                    ),
                };
            }

            return t;
        });

        return {
            c,
            rIdx: [...R],
        };
    };

    const moveHLine = (
        lineY: number,
        delta: number,
        seedLo: number,
        seedHi: number,
    ): { c: number; bIdx: number[] } => {
        let lo = seedLo;
        let hi = seedHi;

        const inBand = (i: number) =>
            ts[i].x < hi - 1 &&
            ts[i].x + ts[i].width > lo + 1;

        const T = new Set<number>();
        const B = new Set<number>();

        let changed = true;

        while (changed) {
            changed = false;

            for (let i = 0; i < n; i++) {
                if (
                    !T.has(i) &&
                    near(
                        ts[i].y + ts[i].height,
                        lineY,
                    ) &&
                    inBand(i)
                ) {
                    T.add(i);

                    lo = Math.min(
                        lo,
                        ts[i].x,
                    );

                    hi = Math.max(
                        hi,
                        ts[i].x + ts[i].width,
                    );

                    changed = true;
                }

                if (
                    !B.has(i) &&
                    near(
                        ts[i].y,
                        lineY + GAP,
                    ) &&
                    inBand(i)
                ) {
                    B.add(i);

                    lo = Math.min(
                        lo,
                        ts[i].x,
                    );

                    hi = Math.max(
                        hi,
                        ts[i].x + ts[i].width,
                    );

                    changed = true;
                }
            }
        }

        if (!B.size) {
            return {
                c: 0,
                bIdx: [],
            };
        }

        let c = delta;

        T.forEach((i) => {
            c = Math.max(
                -(ts[i].height - MIN),
                c,
            );
        });

        B.forEach((i) => {
            c = Math.min(
                ts[i].height - MIN,
                c,
            );
        });

        if (c === 0) {
            return {
                c: 0,
                bIdx: [],
            };
        }

        ts = ts.map((t, i) => {
            if (T.has(i)) {
                return {
                    ...t,
                    height: snap(
                        t.height + c,
                    ),
                };
            }

            if (B.has(i)) {
                return {
                    ...t,
                    y: snap(t.y + c),
                    height: snap(
                        t.height - c,
                    ),
                };
            }

            return t;
        });

        return {
            c,
            bIdx: [...B],
        };
    };

    const cascadeV = (
        rIdx: number[],
        delta: number,
        depth = 0,
    ): void => {
        if (
            depth > 30 ||
            delta === 0 ||
            !rIdx.length
        ) {
            return;
        }

        rIdx.forEach((i) => {
            const farEdge =
                ts[i].x + ts[i].width;

            const {
                c,
                rIdx: nextR,
            } = moveVLine(
                farEdge,
                delta,
                ts[i].y,
                ts[i].y + ts[i].height,
            );

            if (c !== 0) {
                cascadeV(
                    nextR,
                    c,
                    depth + 1,
                );
            }
        });
    };

    const cascadeH = (
        bIdx: number[],
        delta: number,
        depth = 0,
    ): void => {
        if (
            depth > 30 ||
            delta === 0 ||
            !bIdx.length
        ) {
            return;
        }

        bIdx.forEach((i) => {
            const farEdge =
                ts[i].y + ts[i].height;

            const {
                c,
                bIdx: nextB,
            } = moveHLine(
                farEdge,
                delta,
                ts[i].x,
                ts[i].x + ts[i].width,
            );

            if (c !== 0) {
                cascadeH(
                    nextB,
                    c,
                    depth + 1,
                );
            }
        });
    };

    const srcT =
        initialTransforms[nodeId];

    if (!srcT) {
        return {
            transforms: initialTransforms.map(
                (t) => ({ ...t }),
            ),
            appliedDx: 0,
            appliedDy: 0,
        };
    }

    const vLine =
        srcT.x + srcT.width;

    const hLine =
        srcT.y + srcT.height;

    const {
        c: appliedDx,
        rIdx,
    } = moveVLine(
        vLine,
        Math.round(dx),
        srcT.y,
        srcT.y + srcT.height,
    );

    if (appliedDx !== 0) {
        cascadeV(
            rIdx,
            appliedDx,
        );
    }

    const {
        c: appliedDy,
        bIdx,
    } = moveHLine(
        hLine,
        Math.round(dy),
        srcT.x,
        srcT.x + srcT.width,
    );

    if (appliedDy !== 0) {
        cascadeH(
            bIdx,
            appliedDy,
        );
    }

    return {
        transforms: ts,
        appliedDx,
        appliedDy,
    };
}